/** Historique global des démarches et journal d'audit de l'entreprise. */

import { useState } from "react";
import { Link } from "react-router-dom";

import { miscApi } from "../api/endpoints";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
} from "../components/ui";
import { useDocumentTitle, useResource } from "../hooks/useApi";
import { formatDateTime } from "../lib/format";

const PAGE_SIZE = 50;

export function HistoryPage() {
  useDocumentTitle("Historique");
  const [tab, setTab] = useState<"tasks" | "audit">("tasks");
  const [offset, setOffset] = useState(0);

  const history = useResource(
    () => miscApi.history({ limit: PAGE_SIZE, offset }),
    [offset],
    { enabled: tab === "tasks" },
  );
  const audit = useResource(
    () => miscApi.audit({ limit: PAGE_SIZE, offset }),
    [offset],
    { enabled: tab === "audit" },
  );

  const active = tab === "tasks" ? history : audit;

  return (
    <>
      <PageHeader
        title="Historique"
        subtitle="Tout ce qui s'est passé sur votre dossier. Rien n'est effacé."
      />

      <div className="chips" role="tablist" aria-label="Type d'historique">
        <button
          type="button"
          className="chip"
          role="tab"
          aria-selected={tab === "tasks"}
          aria-pressed={tab === "tasks"}
          onClick={() => {
            setTab("tasks");
            setOffset(0);
          }}
        >
          Démarches
        </button>
        <button
          type="button"
          className="chip"
          role="tab"
          aria-selected={tab === "audit"}
          aria-pressed={tab === "audit"}
          onClick={() => {
            setTab("audit");
            setOffset(0);
          }}
        >
          Journal d'audit
        </button>
      </div>

      <Alert tone="info" title="Pourquoi conserver ces traces ?">
        {tab === "tasks"
          ? "L'historique garde chaque changement de statut, chaque échéance modifiée et chaque justificatif rattaché. Une démarche validée conserve ainsi la preuve de ce qui a été fait, et quand."
          : "Le journal d'audit enregistre les événements importants du dossier : connexions, changements de sécurité, validations, exports, restaurations. Aucun secret n'y est écrit."}
      </Alert>

      <ErrorAlert error={active.error} title="Historique indisponible" />

      <Card flush>
        {active.loading ? (
          <LoadingBlock />
        ) : tab === "tasks" ? (
          !history.data || history.data.items.length === 0 ? (
            <EmptyState title="Aucun événement enregistré" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <caption className="visually-hidden">
                  Historique des démarches
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Événement</th>
                    <th scope="col">Détail</th>
                    <th scope="col">Démarche</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.items.map((entry) => (
                    <tr key={entry.id}>
                      <td className="nowrap">{formatDateTime(entry.created_at)}</td>
                      <td>{entry.event_label}</td>
                      <td>
                        {entry.field_name && <strong>{entry.field_name} : </strong>}
                        {entry.old_value && <span>« {entry.old_value} » → </span>}
                        {entry.new_value && <span>« {entry.new_value} »</span>}
                        {entry.note && (
                          <div className="inline-note">{entry.note}</div>
                        )}
                      </td>
                      <td>
                        <Link to={`/demarches/${entry.task_id}`}>Ouvrir</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : !audit.data || audit.data.items.length === 0 ? (
          <EmptyState title="Aucun événement dans le journal" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <caption className="visually-hidden">Journal d'audit</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Action</th>
                  <th scope="col">Résultat</th>
                  <th scope="col">Détail</th>
                </tr>
              </thead>
              <tbody>
                {audit.data.items.map((entry) => (
                  <tr key={entry.id}>
                    <td className="nowrap">{formatDateTime(entry.created_at)}</td>
                    <td>{entry.action_label}</td>
                    <td>
                      {entry.outcome === "success" ? (
                        <span className="badge badge--success">Réussi</span>
                      ) : (
                        <span className="badge badge--danger">Échec</span>
                      )}
                    </td>
                    <td>
                      {entry.summary}
                      {entry.ip_address && (
                        <div className="inline-note mono">{entry.ip_address}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {active.data && active.data.total > PAGE_SIZE && (
        <nav className="row row--between mt-4" aria-label="Pagination">
          <Button
            variant="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Plus récents
          </Button>
          <span className="inline-note">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, active.data.total)} sur{" "}
            {active.data.total}
          </span>
          <Button
            variant="ghost"
            disabled={!active.data.has_more}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Plus anciens
          </Button>
        </nav>
      )}
    </>
  );
}
